import {
  anchoredCumulative,
  sessionAnchor,
  type Cvd2Bar,
  type SessionAnchor,
} from "../cvd2.ts";
import { LANDING_CHART_FLOOR } from "../landingFold.ts";

export const PRICE_PANE = 0;
export const CVD_LINE_PANE = 1;
export const VSPLIT_PANE = 2;

export const PANE_MIN_HEIGHTS = Object.freeze({
  price: 520,
  // ORDER 20C-2 v2 removes the noisy delta/intensity pane; line keeps the former larger share.
  cvdLine: 220,
  vsplit: 90,
});

// R2010 / PANE-CALIBRATION-1 34eb4e6e: landing panes stay proportional at every host.
// Focus keeps the independent PANE_MIN_HEIGHTS contract above.
export const LANDING_PANE_SHARES = Object.freeze({ price: 0.63, cvdLine: 0.26, vsplit: 0.11 });
export const LANDING_PRICE_FLOOR = 360;
export const LANDING_CVD_LINE_FLOOR = 140;
export const LANDING_VSPLIT_FLOOR = 64;
/** A positive sub-2px host is mounted but not laid out yet, so the landing split must wait. */
export const LANDING_HOST_UNLAID_PX = 2;

export interface LandingPaneHeights {
  price: number;
  cvdLine: number;
  vsplit: number;
}

/** Return the landing split, or null while a mounted host is still too tiny to have been laid out. */
export function landingPaneHeights(hostHeight: number): LandingPaneHeights | null {
  if (!Number.isFinite(hostHeight) || hostHeight <= 0) {
    throw new RangeError(`landing panes: host height must be finite and positive; received ${hostHeight}`);
  }
  if (hostHeight < LANDING_HOST_UNLAID_PX) return null;
  if (hostHeight < LANDING_CHART_FLOOR) {
    throw new RangeError(`landing panes: host ${hostHeight}px is below the ${LANDING_CHART_FLOOR}px landing floor`);
  }
  const hostPixels = Math.round(hostHeight);
  const panes = {
    price: Math.round(hostPixels * LANDING_PANE_SHARES.price),
    cvdLine: Math.round(hostPixels * LANDING_PANE_SHARES.cvdLine),
    vsplit: 0,
  };
  panes.vsplit = hostPixels - panes.price - panes.cvdLine;
  if (panes.price < LANDING_PRICE_FLOOR || panes.cvdLine < LANDING_CVD_LINE_FLOOR ||
      panes.vsplit < LANDING_VSPLIT_FLOOR) {
    throw new RangeError(
      `landing panes: host ${hostHeight}px violates floors ` +
      `${LANDING_PRICE_FLOOR}/${LANDING_CVD_LINE_FLOOR}/${LANDING_VSPLIT_FLOOR}`,
    );
  }
  return panes;
}

export interface StudyPoint {
  t: number;
  value: number | null;
}

export interface CvdSegment {
  id: string;
  instrument: string;
  points: StudyPoint[];
}

export interface CvdStudyModel {
  blocked: string | null;
  noData: boolean;
  anchor: SessionAnchor | null;
  segments: CvdSegment[];
  delta: Array<{ t: number; value: number }>;
  readback: CvdRollReadback;
}

export interface CvdRollReadback {
  roll_buckets: number;
  first: number | null;
}

export type CvdDisplayMode = "continuous" | "session";

export interface CvdCandlePoint {
  t: number;
  open: number;
  high: number;
  low: number;
  close: number;
  /** True only when the producer explicitly served all three wick fields as null/absent. */
  wickless: boolean;
}

export interface CvdCandleModel {
  blocked: string | null;
  candles: CvdCandlePoint[];
  whitespace: Array<{ t: number }>;
  wicklessCount: number;
}

/**
 * Convert the producer's cumulative-delta OHLC without interpreting wick geometry.
 *
 * The display anchor is a translation applied to all four ordinates. A legacy pre-033 row is
 * deliberately flat at the served close and flagged `wickless`; `delta` is never consulted, so
 * this renderer cannot fabricate an intra-bar path. Partial/corrupt OHLC refuses the candle leg.
 */
export function buildCvdCandleModel(
  bars: readonly Cvd2Bar[],
  anchorValue = 0,
  upstreamBlocked: string | null = null,
  mode: CvdDisplayMode = "session",
): CvdCandleModel {
  if (upstreamBlocked !== null) {
    return { blocked: upstreamBlocked, candles: [], whitespace: [], wicklessCount: 0 };
  }
  if (!Number.isFinite(anchorValue)) {
    return { blocked: `CVD candle anchor ${anchorValue} is not finite`, candles: [], whitespace: [], wicklessCount: 0 };
  }
  const candles: CvdCandlePoint[] = [];
  const whitespace: Array<{ t: number }> = [];
  let wicklessCount = 0;
  const appliedAnchor = mode === "session" ? anchorValue : 0;
  for (let index = 0; index < bars.length; index += 1) {
    const bar = bars[index];
    if (isRollOrGap(bar)) {
      whitespace.push({ t: bar.t });
      continue;
    }
    const cvd = bar.cvd;
    if (typeof cvd !== "number" || !Number.isFinite(cvd)) {
      whitespace.push({ t: bar.t });
      continue;
    }
    const raw = [bar.cvd_o, bar.cvd_h, bar.cvd_l];
    const absent = raw.map((value) => value === null || value === undefined);
    if (absent.some(Boolean) && !absent.every(Boolean)) {
      return {
        blocked: `partial CVD candle at ${bar.t} — cvd_o/cvd_h/cvd_l must be all present or all null`,
        candles: [],
        whitespace: [],
        wicklessCount: 0,
      };
    }
    if (absent.every(Boolean)) {
      const close = cvd - appliedAnchor;
      candles.push({ t: bar.t, open: close, high: close, low: close, close, wickless: true });
      wicklessCount += 1;
      continue;
    }
    const [open, high, low] = raw as [number, number, number];
    if (![open, high, low].every(Number.isFinite) ||
        high < Math.max(open, cvd) || low > Math.min(open, cvd) || low > high) {
      return {
        blocked: `incoherent CVD candle at ${bar.t} o=${open} h=${high} l=${low} c=${cvd}`,
        candles: [],
        whitespace: [],
        wicklessCount: 0,
      };
    }
    candles.push({
      t: bar.t,
      open: open - appliedAnchor,
      high: high - appliedAnchor,
      low: low - appliedAnchor,
      close: cvd - appliedAnchor,
      wickless: false,
    });
  }
  if (bars.length > 0 && bars.every((bar) => !hasFiniteCvd(bar))) {
    return {
      blocked: "NO DATA — every CVD bucket is null",
      candles: [],
      whitespace,
      wicklessCount: 0,
    };
  }
  return { blocked: null, candles, whitespace, wicklessCount };
}

/**
 * ORDER 65 — **A WICKLESS BAR MAY NOT OWN THE CVD SCALE.** Kept pure so the rule is armable
 * without a browser, the way ORDER 48's page budget is.
 *
 * ⛔ THE DEFECT THIS CURES, MEASURED ON PRODUCTION 2026-09-01: legacy pre-hole rows sit on a
 * different cumulative baseline from the live ones. At the 3h rung 91 of 300 bars were wickless
 * and spanned 285,481 while the measured bars spanned 47,671 — so LWC fitted the axis to the
 * legacy extrema and the live band collapsed to a flat line six times too small to read. The
 * bars were CORRECT and the pane was UNREADABLE.
 *
 * ⇒ This is `LwcPriceCanvas`'s law arriving through a third door — *"an overlay may not drive the
 * price scale ... LWC autoscales a scale from EVERY series on it"*. Same cure, one series inward:
 * supply the range instead of letting the engine fit raw extrema.
 *
 * ⚠ THE BARS ARE STILL DRAWN. This narrows the SCALE, never the data — a wickless bar keeps its
 * flat mark at its served close, it simply stops stretching the axis every other bar shares.
 *
 * ⛔⛔ **THE FALLBACK USED TO RETURN `null` AND THAT WAS A DEFECT, NOT A DESIGN.** The comment that
 * stood here said "the engine's own autoscale must keep the pane". ***I MEASURED IT AND IT IS FALSE.***
 * Driven against the live bundle, provider neutered to return `null`, reproduced at an 80s settle:
 *     cvdCandle:ES:180  priceScale().getVisibleRange()  ->  { from: -0.5, to: 0.5 }
 * ⇒ `null` DOES NOT MEAN "use your default autoscale" TO LIGHTWEIGHT-CHARTS. IT MEANS NO INFO, AND
 * THE SCALE COLLAPSES TO ITS DEGENERATE DEFAULT — a BLANK PANE, the exact outcome the old comment
 * called worse. An all-wickless window is not hypothetical: 49 of 300 bars were wickless at the 6h
 * rung on 2026-09-01, and a window further back is all of them.
 * ⇒ SO THE FALLBACK NOW WIDENS TO EVERY FINITE CANDLE, wickless included. A legacy-fitted scale is a
 * real scale; a degenerate one shows nothing. `null` is reserved for a window with NO finite candle
 * at all, where the degenerate scale is honest because there is nothing to draw.
 * 🔑 ***A LIBRARY'S "no opinion" RETURN IS A BEHAVIOUR TO BE MEASURED, NOT A MEANING TO BE ASSUMED.***
 */
export const CVD_CANDLE_AUTOSCALE_MARGIN_FRACTION = 0.08;
export const CVD_CANDLE_AUTOSCALE_MIN_MARGIN = 1;

export function cvdCandleAutoscaleRange(
  candles: readonly CvdCandlePoint[],
  marginFraction = 0,
  minimumMargin = 0,
): { minValue: number; maxValue: number } | null {
  if (!Number.isFinite(marginFraction) || marginFraction < 0 ||
      !Number.isFinite(minimumMargin) || minimumMargin < 0) {
    throw new RangeError("CVD candle autoscale margins must be finite and non-negative");
  }
  const extent = (measuredOnly: boolean) => {
    let minValue = Number.POSITIVE_INFINITY;
    let maxValue = Number.NEGATIVE_INFINITY;
    for (const candle of candles) {
      if (measuredOnly && candle.wickless) continue;
      if (!Number.isFinite(candle.low) || !Number.isFinite(candle.high)) continue;
      if (candle.low < minValue) minValue = candle.low;
      if (candle.high > maxValue) maxValue = candle.high;
    }
    return Number.isFinite(minValue) && Number.isFinite(maxValue) ? { minValue, maxValue } : null;
  };
  // Measured bars own the scale when any exist; otherwise a legacy-fitted scale beats a blank one.
  const range = extent(true) ?? extent(false);
  if (range === null) return null;
  const span = range.maxValue - range.minValue;
  const margin = span === 0 ? minimumMargin : span * marginFraction;
  return { minValue: range.minValue - margin, maxValue: range.maxValue + margin };
}

export interface GhostInput {
  t: number;
  price: number;
  cvd: number;
  session: string;
}

export interface GhostWallSnapshot {
  price: number;
  availableAt: number;
}

export interface GhostSegment {
  id: string;
  className: "amber" | "violet";
  points: Array<{ t: number; value: number; gap: number }>;
  appearedAt: number;
  resolvedAt: number | null;
  thresholds: { on: number; off: number; minContracts: number; window: number };
  nearWall: boolean;
  /**
   * GHOST-INK-1 hop 1: the SIGNED 10-bar CVD change at the appearance bar - the quantity the detector
   * actually gated on (as its magnitude, netContracts). It is net CVD, not contracts absorbed at a level:
   * nothing in this model measures absorption (RESEARCH-7 5385edc0; ABSORPTION-COUNT-1 is that door).
   */
  netCvd10: number;
}

export const GHOST_THRESHOLDS = Object.freeze({ on: .8, off: .2, minContracts: 600, window: 40 });

function percentile(values: readonly number[], p: number): number | null {
  const sorted = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (!sorted.length) return null;
  const at = Math.max(0, Math.min(1, p)) * (sorted.length - 1);
  const lo = Math.floor(at); const hi = Math.ceil(at);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (at - lo);
}

/**
 * Amendment G: price-scale shadow, admitted only by a knowledge-time expanding gate.
 * The percentile history is strictly before the candidate bar; a full-session percentile would
 * make early events depend on future tape and is the M-LOOKAHEAD mutant.
 */
export function buildGhostSegments(
  inputs: readonly GhostInput[],
  walls: readonly GhostWallSnapshot[] = [],
): GhostSegment[] {
  const output: GhostSegment[] = [];
  let cursor = 0;
  while (cursor < inputs.length) {
    const session = inputs[cursor].session;
    let end = cursor + 1;
    while (end < inputs.length && inputs[end].session === session) end += 1;
    const group = inputs.slice(cursor, end);
    const gaps: number[] = [];
    const shadows: number[] = [];
    for (let i = 0; i < group.length; i += 1) {
      const from = Math.max(0, i - GHOST_THRESHOLDS.window);
      const prices = group.slice(from, i + 1).map((item) => item.price);
      const cvds = group.slice(from, i + 1).map((item) => item.cvd);
      const priceMean = prices.reduce((sum, value) => sum + value, 0) / prices.length;
      const cvdMean = cvds.reduce((sum, value) => sum + value, 0) / cvds.length;
      const priceStd = Math.sqrt(prices.reduce((sum, value) => sum + (value - priceMean) ** 2, 0) / prices.length);
      const cvdStd = Math.sqrt(cvds.reduce((sum, value) => sum + (value - cvdMean) ** 2, 0) / cvds.length);
      const shadow = priceMean + (group[i].cvd - cvdMean) / (cvdStd || 1) * (priceStd || 1);
      shadows.push(shadow);
      gaps.push(shadow - group[i].price);
    }
    let state: "amber" | "violet" | null = null;
    let current: GhostSegment | null = null;
    for (let i = 0; i < group.length; i += 1) {
      const history = gaps.slice(0, i).map(Math.abs).filter(Number.isFinite);
      if (history.length < 30) continue;
      const scale = percentile(history, .9);
      if (scale === null || scale <= 0) continue;
      const normalized = gaps[i] / scale;
      const netCvd10 = group[i].cvd - group[Math.max(0, i - 10)].cvd;
      const netContracts = Math.abs(netCvd10);
      if (state === null && Math.abs(normalized) >= GHOST_THRESHOLDS.on && netContracts >= GHOST_THRESHOLDS.minContracts) {
        state = normalized > 0 ? "amber" : "violet";
        const knownWalls = walls.filter((wall) => wall.availableAt < group[i].t);
        const nearestWall = knownWalls.length
          ? Math.min(...knownWalls.map((wall) => Math.abs(group[i].price - wall.price) / .25))
          : Infinity;
        current = {
          id: `ghost:${output.length}`,
          className: state,
          points: [],
          appearedAt: group[i].t,
          resolvedAt: null,
          thresholds: { ...GHOST_THRESHOLDS },
          nearWall: nearestWall <= 12,
          netCvd10,
        };
        output.push(current);
      }
      if (state !== null && current !== null) {
        current.points.push({ t: group[i].t, value: shadows[i], gap: gaps[i] });
        if (Math.abs(normalized) <= GHOST_THRESHOLDS.off) {
          current.resolvedAt = group[i].t;
          state = null;
          current = null;
        }
      }
    }
    cursor = end;
  }
  return output;
}

/**
 * GHOST-INK-1 hop 1: the ghost's price-axis pill. It reads the SIGNED 10-bar net CVD the detector gated
 * on - "10-bar net CVD +1,240" - never the internal id and never "abs". A non-finite value cannot pass
 * the detector's gate (netContracts >= minContracts), so one reaching here is a broken invariant: it is
 * refused by name, never drawn as a number.
 */
export function ghostAxisTitle(segment: Pick<GhostSegment, "netCvd10">): string {
  const value = segment.netCvd10;
  if (!Number.isFinite(value)) {
    throw new RangeError(`ghost axis title: netCvd10 must be finite; received ${value}`);
  }
  const rounded = Math.round(value);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "-" : "";
  return `Δ10 ${sign}${Math.abs(rounded).toLocaleString("en-US")}`;
}

const EMPTY_ROLL_READBACK: CvdRollReadback = Object.freeze({ roll_buckets: 0, first: null });

const refused = (
  blocked: string,
  noData = false,
  readback: CvdRollReadback = EMPTY_ROLL_READBACK,
): CvdStudyModel => ({
  blocked,
  noData,
  anchor: null,
  segments: [],
  delta: [],
  readback,
});

function hasFiniteCvd(bar: Cvd2Bar): boolean {
  return typeof bar.cvd === "number" && Number.isFinite(bar.cvd);
}

function isRollOrGap(bar: Cvd2Bar): boolean {
  return (typeof bar.instrument_count === "number" && bar.instrument_count > 1) || !hasFiniteCvd(bar);
}

function rollReadback(bars: readonly Cvd2Bar[]): CvdRollReadback {
  const first = bars.find((bar) => isRollOrGap(bar));
  return {
    roll_buckets: bars.filter((bar) => isRollOrGap(bar)).length,
    first: first?.t ?? null,
  };
}

/** Build the exact CVD-line and delta values without creating a second interpretation in the renderer. */
export function buildCvdStudyModel(
  bars: readonly Cvd2Bar[],
  boundaryMs: number | null,
  mode: CvdDisplayMode = "continuous",
): CvdStudyModel {
  const readback = rollReadback(bars);
  if (bars.length > 0 && bars.every((bar) => !hasFiniteCvd(bar))) {
    return refused("NO DATA — every CVD bucket is null", true, readback);
  }
  for (const bar of bars) {
    if (bar.ambiguous === true && !isRollOrGap(bar)) {
      return refused(`CVD bucket ${bar.t} is ambiguous — refusing pooled instruments`);
    }
    if (!isRollOrGap(bar) && (typeof bar.instrument !== "string" || bar.instrument.length === 0)) {
      return refused(`CVD bucket ${bar.t} instrument UNREPORTED — refusing an unlabelled cumulative line`);
    }
  }

  const mutableBars = [...bars];
  const anchor = boundaryMs === null ? null : sessionAnchor(
    mutableBars.filter((bar) => !isRollOrGap(bar)),
    boundaryMs,
  );
  const cumulative = anchoredCumulative(mutableBars, anchor, mode);
  const segments: CvdSegment[] = [];
  let lastInstrument: string | null = null;
  for (let index = 0; index < bars.length; index += 1) {
    const bar = bars[index];
    const followingInstrument = bars.slice(index + 1).find((candidate) => !isRollOrGap(candidate))?.instrument;
    const instrument: string = typeof bar.instrument === "string" && bar.instrument.length > 0
      ? bar.instrument
      : lastInstrument ?? (typeof followingInstrument === "string" ? followingInstrument : "ROLL/GAP");
    const previous = segments.at(-1);
    let segment: CvdSegment;
    if (previous?.instrument !== instrument) {
      segment = {
        id: `cvdCumulative:${instrument}:${segments.length}`,
        instrument,
        points: [],
      };
      segments.push(segment);
    } else {
      segment = previous;
    }
    segment.points.push({ t: bar.t, value: isRollOrGap(bar) ? null : cumulative[index]?.v ?? null });
    if (!isRollOrGap(bar)) lastInstrument = instrument;
  }

  const delta = bars.flatMap((bar) =>
    !isRollOrGap(bar) && typeof bar.delta === "number" && Number.isFinite(bar.delta)
      ? [{ t: bar.t, value: bar.delta }]
      : [],
  );
  return { blocked: null, noData: false, anchor, segments, delta, readback };
}
